import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../Firebase";
import "./Public.css";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtDate(v) {
  if (!v) return "";
  if (typeof v?.toDate === "function") return v.toDate().toLocaleDateString();
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleDateString();
    return v;
  }
  return "";
}

function isoToday() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function money(n, currency = "USD") {
  const num = Number(n);
  if (!isFinite(num)) return "";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
}

function joinAddress(addr) {
  if (!addr) return "";
  const parts = [
    addr.line1,
    addr.line2,
    `${addr.city || ""}${addr.city && addr.state ? ", " : ""}${addr.state || ""} ${addr.postal || ""}`.trim(),
    addr.country,
  ]
    .map((s) => String(s || "").trim())
    .filter(Boolean);
  return parts.join("\n");
}

async function resolveStorageUrl(path) {
  if (!path) return "";
  if (String(path).startsWith("http")) return path;
  return await getDownloadURL(storageRef(storage, path));
}

/**
 * If totals aren’t stored, we compute from groups + pricing + payments.
 * (I recommend storing totals on invoice to keep print stable.)
 */
function computeTotals(groups, pricing, payments) {
  const taxMode = pricing?.taxMode || "exclusive";
  const taxRate = safeNum(pricing?.taxRate);
  const discounts = Array.isArray(pricing?.discounts) ? pricing.discounts : [];
  const fees = Array.isArray(pricing?.fees) ? pricing.fees : [];

  let subtotal = 0;
  let taxableSubtotal = 0;

  for (const g of groups || []) {
    for (const it of g.items || []) {
      const line = safeNum(it.qty) * safeNum(it.unitPrice);
      subtotal += line;
      if (it.taxable) taxableSubtotal += line;
    }
  }

  let feesTotal = 0;
  let taxableFees = 0;
  for (const f of fees) {
    const amt = safeNum(f.amount);
    feesTotal += amt;
    if (f.taxable) taxableFees += amt;
  }

  let discountTotal = 0;
  for (const d of discounts) {
    const type = d.type || "percent";
    const value = safeNum(d.value);
    discountTotal += type === "percent" ? subtotal * value : value;
  }
  discountTotal = Math.max(0, Math.min(discountTotal, subtotal));

  const taxableRatio = subtotal > 0 ? taxableSubtotal / subtotal : 0;
  const taxableDiscountShare = discountTotal * taxableRatio;

  const taxableAfterDiscount = Math.max(0, taxableSubtotal - taxableDiscountShare);
  const subtotalAfterDiscount = Math.max(0, subtotal - discountTotal);

  let taxTotal = 0;
  if (taxMode === "exclusive") taxTotal = (taxableAfterDiscount + taxableFees) * taxRate;

  const total = subtotalAfterDiscount + feesTotal + taxTotal;

  const paymentsTotal = (payments || []).reduce((acc, p) => acc + safeNum(p.amount), 0);
  const balanceDue = Math.max(0, total - paymentsTotal);

  return { subtotal, discountTotal, feesTotal, taxTotal, total, paymentsTotal, balanceDue };
}

export default function InvoicePublicView() {
  const { blobId } = useParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [inv, setInv] = useState(null);
  const [resolvedPaths, setResolvedPaths] = useState({}); // path->url

  useEffect(() => {
    document.title = "Invoice | Crowned Roofing";
  }, []);

  const handlePrint = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Let layout settle before opening the print dialog.
    requestAnimationFrame(() => {
      window.print();
    });
  };

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");
      setInv(null);

      try {
        const snap = await getDoc(doc(db, "publicInvoices", blobId));
        if (!snap.exists()) throw new Error("Invoice not found.");

        const data = { id: snap.id, ...snap.data() };
        if (!alive) return;

        setInv(data);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load invoice.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [blobId]);

  const issuer = inv?.issuer || {};
  const customer = inv?.customer || {};
  const site = inv?.site || {};
  const groups = inv?.groups || [];
  const pricing = inv?.pricing || {};
  const payments = inv?.payments || [];
  const currency = inv?.currency || "USD";
  const payment = inv?.payment || {};
  const warranty = inv?.warranty || {};

  const totals = useMemo(() => {
    if (!inv) return null;
    if (inv.totals && typeof inv.totals.balanceDue !== "undefined") return inv.totals;
    return computeTotals(groups, pricing, payments);
  }, [inv, groups, pricing, payments]);

  const showDiscount = (pricing.discounts || []).some((d) => safeNum(d.value) > 0);
  const showFees = (pricing.fees || []).some((f) => safeNum(f.amount) > 0);
  const showTax = (pricing.taxMode || "exclusive") === "exclusive" && safeNum(pricing.taxRate) > 0;

  // Resolve storage paths for attachments + item photos
  useEffect(() => {
    let alive = true;

    async function collectAndResolve() {
      if (!inv) return;
      const paths = new Set();

      (inv.attachments || []).forEach((a) => a?.path && paths.add(a.path));

      (inv.groups || []).forEach((g) => {
        (g.items || []).forEach((it) => {
          (it.photos || []).forEach((p) => {
            if (p?.path) paths.add(p.path);
            if (p?.url) paths.add(p.url);
          });
        });
      });

      const unresolved = [...paths].filter((p) => !resolvedPaths[p]);
      if (!unresolved.length) return;

      const next = {};
      for (const p of unresolved) {
        try {
          next[p] = await resolveStorageUrl(p);
        } catch {
          // ignore
        }
      }

      if (!alive) return;
      setResolvedPaths((prev) => ({ ...prev, ...next }));
    }

    collectAndResolve();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv]);

  if (loading) {
    return (
      <div className="invWrap">
        <div className="invCard">
          <div className="invMuted">Loading invoice…</div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="invWrap">
        <div className="invCard">
          <h1 className="invH1">Invoice</h1>
          <div className="invError">{err}</div>
        </div>
      </div>
    );
  }

  if (!inv) return null;

  const balanceDue = totals?.balanceDue ?? 0;
  const documentTitle =
    String(inv.status || "").toLowerCase() === "paid" || safeNum(balanceDue) <= 0
      ? "Receipt"
      : "Invoice";

  return (
    <div className="invWrap">
      <div className="noPrint">
        <Header />
      </div>
      {/* Non-print toolbar */}
      <div className="invToolbar noPrint">
        <div className="invToolbarInner">
          <div className="invToolbarLeft">
            <div className="invTiny">Blob ID</div>
            <div className="invMono">{inv.id}</div>
          </div>

          <div className="invToolbarRight">
            <button className="invBtn" onClick={handlePrint}>
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      <div className="invDoc">
        <div className="invCard invCardPrint">
          {/* HEADER */}
          <header className="invHeader">
            <div className="invBrand">
              <div className="invCompany">{issuer.companyName || "Crowned Roofing"}</div>
              <div className="invMeta">
                <div>{issuer.website || ""}</div>
                <div>{issuer.email || ""}</div>
                <div>{issuer.phone || ""}</div>
              </div>
            </div>

            <div className="invHeaderRight">
              <div className="invTitle">{documentTitle}</div>
              <div className="invHeaderGrid">
                <div className="invTiny">Invoice #</div>
                <div className="invValue">{inv.invoiceNumber || inv.id}</div>

                <div className="invTiny">Issue Date</div>
                <div className="invValue">{fmtDate(inv.issueDate) || "—"}</div>

                <div className="invTiny">Due Date</div>
                <div className="invValue">{fmtDate(inv.dueDate) || "—"}</div>

                <div className="invTiny">Status</div>
                <div className="invValue">{String(inv.status || "open")}</div>
              </div>
            </div>
          </header>

          {/* BILL TO / SITE */}
          <section className="invTwoCol invBreakAfter">
            <div className="invBlock">
              <div className="invBlockTitle">Bill To</div>
              <div className="invBlockBody">
                <div className="invStrong">{customer.companyName || customer.name || "—"}</div>
                {customer.name && customer.companyName ? (
                  <div className="invMuted">{customer.name}</div>
                ) : null}
                <div className="invLine">{customer.phone || ""}</div>
                <div className="invLine">{customer.email || ""}</div>
                <pre className="invPre">{joinAddress(customer.billingAddress)}</pre>
              </div>
            </div>

            <div className="invBlock">
              <div className="invBlockTitle">Site</div>
              <div className="invBlockBody">
                <div className="invStrong">{site.propertyName || "—"}</div>
                <pre className="invPre">{joinAddress(site.siteAddress)}</pre>
                {inv.source?.estimateId ? (
                  <div className="invMuted" style={{ marginTop: 6 }}>
                    Source Estimate: {inv.source.estimateId}
                    {inv.source.selectedOptionId ? ` (Option ${inv.source.selectedOptionId})` : ""}
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          {/* LINE ITEMS */}
          <section className="invScope">
            {groups.map((g) => (
              <div className="invGroup" key={g.groupId || g.title}>
                <div className="invGroupHead">
                  <div className="invGroupTitle">{g.title || g.groupId || "Scope"}</div>
                  {g.notes ? <div className="invMuted">{g.notes}</div> : null}
                </div>

                <table className="invTable">
                  <thead>
                    <tr>
                      <th className="colDesc">Description</th>
                      <th className="colQty">Qty</th>
                      <th className="colUnit">Unit</th>
                      <th className="colRate">Unit Price</th>
                      <th className="colTotal">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(g.items || []).map((it) => {
                      const lineTotal = safeNum(it.qty) * safeNum(it.unitPrice);
                      return (
                        <tr key={it.itemId || it.name}>
                          <td className="colDesc">
                            <div className="invStrong">{it.name || it.itemId}</div>
                            {it.description ? <div className="invMuted">{it.description}</div> : null}

                            {(it.photos || []).length ? (
                              <div className="invPhotoRow">
                                {it.photos.slice(0, 3).map((p, idx) => {
                                  const src = p.url || resolvedPaths[p.path] || resolvedPaths[p.url] || "";
                                  if (!src) return null;
                                  return (
                                    <img
                                      key={idx}
                                      src={src}
                                      alt={p.caption || "Invoice photo"}
                                      className="invPhoto"
                                      loading="lazy"
                                    />
                                  );
                                })}
                              </div>
                            ) : null}
                          </td>
                          <td className="colQty">{safeNum(it.qty)}</td>
                          <td className="colUnit">{it.unit || ""}</td>
                          <td className="colRate">{money(it.unitPrice, currency)}</td>
                          <td className="colTotal">{money(lineTotal, currency)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </section>

          {/* TOTALS + PAYMENTS */}
          <section className="invTotals invBreakAfter">
            <div className="invTotalsRow">
              <div className="invBalance">
                <div className="invTiny">Balance Due</div>
                <div className="invBalanceAmt">{money(balanceDue, currency)}</div>
              </div>

              <div className="invTotalsBox">
                <div className="row">
                  <div>Subtotal</div>
                  <div>{money(totals?.subtotal, currency)}</div>
                </div>

                {showDiscount ? (
                  <div className="row">
                    <div>Discounts</div>
                    <div>-{money(totals?.discountTotal, currency)}</div>
                  </div>
                ) : null}

                {showFees ? (
                  <div className="row">
                    <div>Fees</div>
                    <div>{money(totals?.feesTotal, currency)}</div>
                  </div>
                ) : null}

                {showTax ? (
                  <div className="row">
                    <div>Tax</div>
                    <div>{money(totals?.taxTotal, currency)}</div>
                  </div>
                ) : null}

                <div className="row grand">
                  <div>Total</div>
                  <div>{money(totals?.total, currency)}</div>
                </div>

                {(payments || []).length ? (
                  <>
                    <div className="row">
                      <div>Payments</div>
                      <div>-{money(totals?.paymentsTotal, currency)}</div>
                    </div>
                    <div className="row grand">
                      <div>Balance Due</div>
                      <div>{money(totals?.balanceDue, currency)}</div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </section>

          {/* PAYMENT HISTORY + RECEIPT DETAILS + WARRANTY */}
          <section className="invThreeCol">
            <div className="invBlock">
              <div className="invBlockTitle">Payment History</div>
              {(payments || []).length ? (
                <div className="invPayList">
                  {payments.map((p, i) => (
                    <div key={i} className="invPayRow">
                      <div>
                        <div className="invStrong">{money(p.amount, currency)}</div>
                        <div className="invMuted">{p.method || ""}{p.note ? ` — ${p.note}` : ""}</div>
                      </div>
                      <div className="invMuted">{fmtDate(p.at) || ""}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="invMuted">No payments recorded.</div>
              )}
            </div>

            <div className="invBlock">
              <div className="invBlockTitle">Receipt Notes</div>
              <div className="invMuted">{inv.notes || "—"}</div>
              {Array.isArray(payment.paymentMethods) && payment.paymentMethods.length ? (
                <>
                  <div className="invBlockTitle" style={{ marginTop: 14 }}>Payment Methods</div>
                  <ul className="invCompactList">
                    {payment.paymentMethods.map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </>
              ) : null}
              {payment.instructions ? (
                <>
                  <div className="invBlockTitle" style={{ marginTop: 14 }}>Payment Instructions</div>
                  <div className="invMuted">{payment.instructions}</div>
                </>
              ) : null}
              {payment.lateFeePolicy ? (
                <>
                  <div className="invBlockTitle" style={{ marginTop: 14 }}>Late Fee Policy</div>
                  <div className="invMuted">{payment.lateFeePolicy}</div>
                </>
              ) : null}
            </div>

            <div className="invBlock">
              <div className="invBlockTitle">Warranty</div>
              {warranty.type || warranty.term || (Array.isArray(warranty.details) && warranty.details.length) ? (
                <>
                  {warranty.type ? <div className="invStrong">{warranty.type}</div> : null}
                  {warranty.term ? <div className="invMuted">{warranty.term}</div> : null}
                  {Array.isArray(warranty.details) && warranty.details.length ? (
                <ul className="invCompactList">
                      {warranty.details.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
                  ) : null}
                </>
              ) : (
                <div className="invMuted">—</div>
              )}
            </div>
          </section>

          {/* Attachments (screen only) */}
          {Array.isArray(inv.attachments) && inv.attachments.length ? (
            <section className="invAttachments noPrint">
              <div className="invBlockTitle">Attachments</div>
              <div className="invAttachList">
                {inv.attachments.map((a, i) => {
                  const href = a.url || resolvedPaths[a.path] || "";
                  return (
                    <a
                      key={i}
                      className="invAttach"
                      href={href || "#"}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => { if (!href) e.preventDefault(); }}
                    >
                      <div className="invStrong">{a.label || a.path}</div>
                      <div className="invMuted">{a.caption || a.type || ""}</div>
                    </a>
                  );
                })}
              </div>
            </section>
          ) : null}

          <footer className="invFooter">
            <div className="invMuted">
              {issuer.companyName || "Crowned Roofing"} — {issuer.email || ""}
            </div>
            <div className="invTiny">
              Generated: {fmtDate(inv.updatedAt) || fmtDate(inv.createdAt) || isoToday()}
            </div>
          </footer>
        </div>
      </div>
      <div className="noPrint">
        <Footer />
      </div>
    </div>
  );
}
